import { useEffect, useState, useCallback } from 'react';
import { Loader2, ImagePlus } from 'lucide-react';
import PostCard from '@/components/social/PostCard';
import CreatePostModal from '@/components/social/CreatePostModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** Vendor's social wall — shows their posts. If the viewer IS this user and is
 *  eligible to post, show a "Create Post" composer card on top (Facebook-style). */
export default function VendorPostsWall({ userId, viewer }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [canPost, setCanPost] = useState(false);
  const isOwner = viewer && viewer.user_id === userId;

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const headers = viewer ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {};
      const res = await fetch(`${API}/social/users/${userId}/posts?limit=30`, { headers });
      if (res.ok) setPosts((await res.json()).posts || []);
    } finally { setLoading(false); }
  }, [userId, viewer]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isOwner) { setCanPost(false); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/social/me/can-post`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (res.ok) setCanPost((await res.json()).can_post);
      } catch {}
    })();
  }, [isOwner]);

  const onCreated = (post) => setPosts((p) => [post, ...p]);
  const onDeleted = (id) => setPosts((p) => p.filter((x) => x.post_id !== id));

  return (
    <div className="max-w-2xl mx-auto" data-testid="vendor-posts-wall">
      {/* Composer card — owner only */}
      {isOwner && canPost && (
        <button
          onClick={() => setShowCreate(true)}
          className="w-full mb-6 rounded-2xl bg-[#13153A]/80 border border-fuchsia-500/20 hover:border-fuchsia-400/40 p-4 flex items-center gap-3 text-left transition-all hover:shadow-[0_8px_30px_-10px_rgba(217,70,239,0.5)]"
          data-testid="vendor-post-composer"
        >
          {viewer?.picture ? (
            <img src={viewer.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-fuchsia-500/30 flex items-center justify-center text-white font-bold">
              {(viewer?.name || '?')[0]}
            </div>
          )}
          <span className="flex-1 text-zinc-300 text-sm">Share something with your followers…</span>
          <span className="px-3 py-1.5 rounded-full bg-yellow-400 text-black text-xs font-bold flex items-center gap-1">
            <ImagePlus size={13} /> Post
          </span>
        </button>
      )}

      {loading && posts.length === 0 ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-fuchsia-400" /></div>
      ) : posts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center">
          <p className="text-white font-semibold">No posts yet</p>
          <p className="text-zinc-400 text-xs mt-1">{isOwner ? 'Share your first post — image or short video.' : 'When this vendor posts, it will show here.'}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {posts.map((p) => (
            <PostCard key={p.post_id} post={p} viewer={viewer} onChange={load} onDelete={onDeleted} />
          ))}
        </div>
      )}

      <CreatePostModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={onCreated} viewer={viewer} />
    </div>
  );
}
